/**
 * Pure display-mapping helpers for StorefrontOrderConfirmation.vue.
 *
 * Plan 2 (storefront buyer completion) section 2.5: the confirmation
 * page renders either the checkout-pushed query params (same-session
 * navigation) or, when those are absent (direct link / email link),
 * a summary fetched from GET /api/orders/<id>.
 *
 * Extracted as pure functions so the mapping stays testable without
 * mounting Vuetify. Behavioural tests:
 * __tests__/order-confirmation-direct-load.spec.js.
 */

function moneyOrNull(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n.toFixed(2);
}

/**
 * Total free-item discount frozen on an order's lines, in DOLLARS.
 *
 * Highest-priced-item-free (AH, https://trello.com/c/T756dpsG). Sums the
 * persisted per-line ``order_items.free_item_discount_cents`` and NEVER
 * re-derives the rule -- the same source and the same reasoning as
 * ``api/src/models/account.py::_frozen_free_item_discount_cents``. An
 * order's charge must not change because the store's configuration did,
 * and a second answer to "which item is free" is what an earlier PR
 * consolidated away.
 *
 * UNITS: the column is integer CENTS while every order money column is
 * Float DOLLARS (documented mismatch at ``models/order.py:259``/:285).
 * The division by 100 happens HERE and exactly once -- missing it renders
 * ``$13500.00`` for ``$135.00``.
 *
 * Returns null (not 0) when nothing is discounted, so the caller's
 * ``v-if`` hides the row rather than printing "-$0.00" on every order in
 * every store that does not run the rule. Legacy lines predating the
 * column read as 0, i.e. "not discounted" rather than unknown.
 */
function freeItemDiscountFromItems(orderItems) {
  const items = Array.isArray(orderItems) ? orderItems : [];
  const cents = items.reduce(
    (sum, item) => sum + (Number(item?.free_item_discount_cents) || 0),
    0
  );
  return moneyOrNull(cents / 100);
}

/**
 * True when the route query carries the checkout-pushed summary.
 * orderNumber is the sentinel — checkout always includes it.
 */
export function hasQuerySummary(query) {
  return Boolean(query && query.orderNumber);
}

/**
 * The legacy display shape, read straight from the query params
 * (values pass through unchanged — checkout pre-formats them).
 */
export function summaryFromQuery(query) {
  const q = query || {};
  return {
    orderNumber: q.orderNumber || null,
    itemCount: q.itemCount || null,
    orderTotal: q.total || null,
    orderAllowance: q.allowance || null,
    orderCharged: q.charged || null,
    // Set by the DEFERRED (batched) checkout path: the card is saved, not
    // charged, so `charged` is the amount that WILL be charged. #424.
    chargeDeferred: q.deferred === "1" || q.deferred === 1 || q.deferred === true,
    // Already DOLLARS at the push site (StorefrontCart's `freeItemDiscount`
    // is `unitCents / 100`), so it passes through unchanged like the other
    // pre-formatted money params -- no second conversion here.
    orderFreeItemDiscount: q.freeItemDiscount || null,
  };
}

/**
 * The same display shape derived from a fetched Order row (jsonapi-vuex
 * normalized) plus its order_items. Numeric columns may serialize as
 * strings (SAFRS Decimal); zero/absent money rows map to null so the
 * template hides them, matching the query-param behavior where
 * checkout omits falsy values.
 */
export function summaryFromOrder(order, orderItems) {
  if (!order) {
    return {
      orderNumber: null,
      itemCount: null,
      orderTotal: null,
      orderAllowance: null,
      orderCharged: null,
      chargeDeferred: false,
      orderFreeItemDiscount: null,
    };
  }

  const items = Array.isArray(orderItems) ? orderItems : [];
  const itemCount = items.reduce(
    (sum, item) => sum + (Number(item?.quantity) || 0),
    0
  );

  return {
    // The STORE-FORMATTED order number ("AC-2026-01001"), not the DB id.
    // This previously read the id, so the confirmation page showed "#2" for
    // an order whose real number was AC-2026-01001 (live AdventHealth
    // store). ``orders.order_number`` is nullable -- a legible "not yet
    // assigned" marker (models/order.py) -- so the id remains the fallback
    // rather than showing the shopper a blank order number. An empty string
    // is treated as absent for the same reason.
    orderNumber: order.order_number || order._jv?.id || order.id || null,
    itemCount: itemCount > 0 ? itemCount : null,
    orderTotal: moneyOrNull(order.total),
    orderAllowance: moneyOrNull(order.used_allowance),
    orderCharged: moneyOrNull(order.charged_amount),
    // A batched order is charged when its batch is released; until then the
    // figure is the amount to be charged, not an amount charged (#424).
    chargeDeferred:
      order.status === "awaiting_batch_charge" ||
      order.payment_state === "pending_capture",
    // Derived from the LINES, not the order: the discount is frozen
    // per-line and `orders` has no column for it.
    orderFreeItemDiscount: freeItemDiscountFromItems(items),
  };
}
